import type { ProjectGroup } from '@/deploy/types';

// ============================================================
//  Groupe de jeu des serveurs de la page Environnements (NationsGlory seulement).
//
//  Le CICD renvoie `group` = RECUBE_GROUP du serveur (normalisé, gardé même inconnu), et rien
//  pour un projet sans groupes de jeu : pour Paladium, aucune fonction ici n'a d'effet visible.
//  Un groupe inconnu reçoit les API Principales (endpoint public) mais n'est vu d'aucun
//  satellite filtré par groupe (project.group, lot 4a) : la page le signale.
// ============================================================

export const GROUP_FILTER_ALL = 'all';
export const GROUP_FILTER_NONE = '__none__';
export const GROUP_FILTER_UNKNOWN = '__unknown__';

export type InstanceGroupState =
  /** RECUBE_GROUP vide ou absent. */
  | { kind: 'none' }
  /** Clé d'un groupe du projet. */
  | { kind: 'known'; group: ProjectGroup }
  /** Clé absente des groupes du projet (faute de frappe, groupe retiré du CICD). */
  | { kind: 'unknown'; key: string }
  /** Groupes du projet non chargés : aucun verdict, la clé est affichée telle quelle. */
  | { kind: 'unverified'; key: string };

/** Même normalisation que le CICD (?group=, RECUBE_GROUP) : rognée, en minuscules, null si vide. */
export function normalizeInstanceGroup(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const key = raw.trim().toLowerCase();
  return key === '' ? null : key;
}

/** État du groupe d'un serveur ; `groups` null = liste non chargée (jamais « inconnu » alors). */
export function instanceGroupState(raw: string | null | undefined, groups: ProjectGroup[] | null): InstanceGroupState {
  const key = normalizeInstanceGroup(raw);
  if (key === null) return { kind: 'none' };
  if (groups === null) return { kind: 'unverified', key };
  const group = groups.find((g) => g.key === key);
  return group ? { kind: 'known', group } : { kind: 'unknown', key };
}

/** Le serveur passe le filtre de groupe (`all`, une clé, sans groupe, groupe inconnu). */
export function matchesGroupFilter(state: InstanceGroupState, filter: string): boolean {
  if (filter === GROUP_FILTER_ALL) return true;
  if (filter === GROUP_FILTER_NONE) return state.kind === 'none';
  if (filter === GROUP_FILTER_UNKNOWN) return state.kind === 'unknown';
  if (state.kind === 'known') return state.group.key === filter;
  return state.kind === 'unverified' && state.key === filter;
}

export interface GroupFilterChip {
  key: string;
  kind: 'all' | 'group' | 'none' | 'unknown';
  label: string;
  count: number;
  /** Couleur du groupe (puce `group` seulement), absente s'il n'en a pas. */
  color?: string;
}

/**
 * Puces du filtre : Tous, chaque groupe du projet (dans son ordre, même à 0), Sans groupe, puis
 * « Groupe inconnu » seulement s'il y en a. Vide si le projet n'a aucun groupe.
 */
export function groupFilterChips(instances: { group?: string }[], groups: ProjectGroup[]): GroupFilterChip[] {
  if (groups.length === 0) return [];
  const states = instances.map((i) => instanceGroupState(i.group, groups));
  const count = (filter: string) => states.filter((s) => matchesGroupFilter(s, filter)).length;
  const chips: GroupFilterChip[] = [{ key: GROUP_FILTER_ALL, kind: 'all', label: 'Tous', count: instances.length }];
  for (const g of groups) {
    chips.push({ key: g.key, kind: 'group', label: g.label || g.key, count: count(g.key), ...(g.color ? { color: g.color } : {}) });
  }
  chips.push({ key: GROUP_FILTER_NONE, kind: 'none', label: 'Sans groupe', count: count(GROUP_FILTER_NONE) });
  const unknown = count(GROUP_FILTER_UNKNOWN);
  if (unknown > 0) chips.push({ key: GROUP_FILTER_UNKNOWN, kind: 'unknown', label: 'Groupe inconnu', count: unknown });
  return chips;
}

/** Filtre gardé s'il a encore une puce, sinon retour à « Tous » (groupe retiré, inconnus corrigés). */
export function validGroupFilter(filter: string, chips: GroupFilterChip[]): string {
  return chips.some((c) => c.key === filter) ? filter : GROUP_FILTER_ALL;
}

/** Serveurs à RECUBE_GROUP inconnu : nombre et clés distinctes (dans l'ordre d'apparition). */
export function unknownGroups(instances: { group?: string }[], groups: ProjectGroup[] | null): { count: number; keys: string[] } {
  const keys: string[] = [];
  let count = 0;
  for (const i of instances) {
    const s = instanceGroupState(i.group, groups);
    if (s.kind !== 'unknown') continue;
    count++;
    if (!keys.includes(s.key)) keys.push(s.key);
  }
  return { count, keys };
}

/** Texte du bandeau d'alerte, null s'il n'y a aucun serveur à groupe inconnu. */
export function unknownGroupsMessage(unknown: { count: number; keys: string[] }, groups: ProjectGroup[]): string | null {
  if (unknown.count === 0) return null;
  const servers = unknown.count > 1 ? `${unknown.count} serveurs ont` : '1 serveur a';
  const known = groups.length === 0 ? 'aucun groupe déclaré' : `groupes du projet : ${groups.map((g) => g.key).join(', ')}`;
  return `${servers} un RECUBE_GROUP inconnu du CICD (${unknown.keys.join(', ')} ; ${known}). `
    + 'Ils reçoivent les API Principales et aucun satellite filtré par groupe ne les affiche en Supervision : '
    + 'corrige la variable dans Pterodactyl, ou déclare le groupe dans Settings → Groupes de jeu.';
}

/** Libellé d'un groupe pour un serveur ou un dev env : celui du projet, sinon la clé. */
export function groupDisplay(raw: string | null | undefined, groups: ProjectGroup[] | null): Pick<ProjectGroup, 'label' | 'color'> | null {
  const state = instanceGroupState(raw, groups);
  if (state.kind === 'none') return null;
  if (state.kind === 'known') return { label: state.group.label || state.group.key, color: state.group.color };
  return { label: state.key };
}
